import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { eerstePaintHtml, vergeetEerstePaint } from "@/lib/first-paint";

/**
 * Suspense-fallback voor route-chunks.
 *
 * Twee gevallen:
 *
 * 1. **Meteen na het opstarten**, op de pagina waarop de bezoeker binnenkwam. Die pagina staat al
 *    volledig in de HTML en stond dus al op het scherm. We tonen 'm hier onveranderd verder, zodat
 *    React 'm niet eerst weggooit (zie lib/first-paint). Geen witte flits.
 *
 * 2. **Bij doorklikken.** Dan is er niets om vast te houden en tonen we de eerste ~400 ms niets
 *    (de meeste chunks laden < 200 ms + idle-prefetch, dus een spinner zou alleen maar opflitsen),
 *    daarna een rustige spinner voor trage verbindingen.
 */
export function RouteFallback() {
  const { pathname } = useLocation();
  const vastgehouden = eerstePaintHtml(pathname);
  const [show, setShow] = useState(false);

  // Zodra de echte route getekend is verdwijnt deze fallback — dan mag de snapshot weg. Zou die
  // blijven staan, dan kreeg je 'm bij terugnavigeren naar dezelfde pagina opnieuw te zien.
  useEffect(() => vergeetEerstePaint, []);

  useEffect(() => {
    if (vastgehouden) return;
    const id = setTimeout(() => setShow(true), 400);
    return () => clearTimeout(id);
  }, [vastgehouden]);

  if (vastgehouden) {
    // Inert: dit is een plaatje van de pagina zonder klikgedrag, en het staat er hooguit een paar
    // honderd milliseconden. aria-busy vertelt schermlezers dat de echte versie nog komt.
    return <div aria-busy="true" dangerouslySetInnerHTML={{ __html: vastgehouden }} />;
  }

  return (
    <div className="flex min-h-[60vh] items-center justify-center bg-white" role="status" aria-live="polite">
      {show && (
        <span className="inline-flex items-center gap-2 text-sm font-medium text-primary-600">
          <svg className="h-5 w-5 animate-spin text-primary-600" viewBox="0 0 24 24" fill="none" aria-hidden>
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Laden…
        </span>
      )}
      <span className="sr-only">Pagina laden…</span>
    </div>
  );
}
